import React from 'react'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSignUpMutation } from '../../../queries/useSignupMutation'
import {
  Button,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
} from 'ui'
import { Signup } from './InitialSignUp'
import toast from 'react-hot-toast'

const confirmAccountSchema = yup.object({
  emailAddress: yup.string().required().email(),
  password: yup.string().required().min(8),
  passwordConfirm: yup
    .string()
    .required()
    .oneOf([yup.ref('password')], 'must match password')
    .required('Password confirm is required'),
  passwordHint: yup.string().required(),
})
export type ConfirmSignup = yup.InferType<typeof confirmAccountSchema>

const FormView: React.FC<{
  defaultValues: ConfirmSignup
  profileValues: Signup
}> = ({ defaultValues, profileValues }) => {
  const form = useForm<ConfirmSignup>({
    defaultValues: {
      ...{
        emailAddress: '',
        password: '',
        passwordConfirm: '',
        passwordHint: '',
      },
      ...defaultValues,
    } as ConfirmSignup,
    reValidateMode: 'onChange',
    resolver: yupResolver(confirmAccountSchema as any) as any,
  })

  const { mutateAsync: signUp } = useSignUpMutation()

  const handleSubmitForm = async (values: ConfirmSignup) => {
    console.log('values', values)
    try {
      await signUp({
        emailAddress: values.emailAddress as string,
        password: values.password as string,
        passwordHint: values.passwordHint as string,
        userRole: profileValues.userRole as string,
        companyName: profileValues.companyName as string,
        schoolId: profileValues.schoolId as string,
        firstName: profileValues.firstName as string,
        lastName: profileValues.lastName as string,
        phone: profileValues.phone as string,
        communityCode: profileValues.communityCode as string,
      })
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmitForm)} className="mt-8">
        <div className="flex-row flex mt-4">
          <FormField
            control={form.control}
            name="emailAddress"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input placeholder="my@emailaddress.com" {...field} />
                </FormControl>
                {profileValues.userRole === 'EDUCATOR' && (
                  <p className="text-xs text-muted-foreground mt-1 animate-pulse-subtle border-l-2 border-primary pl-2 bg-blue-50/30 py-1 rounded transition-all transform hover:scale-[1.01] duration-300">
                    For better verification and communication, we recommend
                    using your{' '}
                    <span className="font-semibold text-primary">
                      official school email address
                    </span>{' '}
                    if available.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex-row flex mt-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} type="password" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex-row flex mt-4">
          <FormField
            control={form.control}
            name="passwordConfirm"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} type="password" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex-row flex mt-4">
          <FormField
            control={form.control}
            name="passwordHint"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Hint</FormLabel>
                <FormControl>
                  <Input placeholder="In case you forget..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex-row flex mt-8 w-full">
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            variant="default"
            className="ml-auto"
          >
            {form.formState.isSubmitting ? (
              <Indicators.Spinner
                size="lg"
                className="mt-auto mr-2 mb-auto ml-auto"
              />
            ) : null}
            <span>Confirm my Account &amp; Build Profile</span>
          </Button>
        </div>
      </form>
    </Form>
  )
}

export const ConfirmAccount = FormView
